import React, { useState, useEffect, useCallback } from 'react';
import { auth, db } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import {
  doc, getDoc, getDocs, collectionGroup
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { Card, CardContent } from './components/ui/card';
import { Button } from './components/ui/button';
import { Badge } from './components/ui/badge';
import {
  Headphones, BookOpen, ShoppingCart, ArrowLeft, Loader2, CheckCircle, Calendar
} from 'lucide-react';

const functions = getFunctions(undefined, 'europe-west2');

// Stripe publishable key — safe to expose client-side
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

/* ═══════════════════════════════════════════
   PRE-ORDER GATE
   Handles card authorisation for pre-orders.
   Routes: /preorder/ebook/{id} and /preorder/audiobook/{id}
═══════════════════════════════════════════ */

export default function PreOrderGate({ contentType, contentId }) {
  const [user, setUser] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [content, setContent] = useState(null);
  const [authorName, setAuthorName] = useState('');
  const [status, setStatus] = useState('loading'); // loading | ready | success | error
  const [error, setError] = useState(null);
  const [clientSecret, setClientSecret] = useState(null);
  const [preOrderId, setPreOrderId] = useState(null);

  const isAudiobook = contentType === 'audiobook';
  const collName = isAudiobook ? 'audiobooks' : 'ebooks';
  const authorId = new URLSearchParams(window.location.search).get('authorId');

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, u => {
      setUser(u);
      setAuthReady(true);
    });
    return unsub;
  }, []);

  const findContent = useCallback(async () => {
    // Try direct lookup if authorId is provided
    if (authorId) {
      const contentRef = doc(db, 'authors', authorId, collName, contentId);
      const snap = await getDoc(contentRef);
      if (snap.exists()) {
        const data = { id: snap.id, authorId, ...snap.data() };
        try {
          const authorSnap = await getDoc(doc(db, 'authors', authorId));
          if (authorSnap.exists()) {
            const a = authorSnap.data();
            setAuthorName(a.penName || a.name || '');
          }
        } catch { /* skip */ }
        setContent(data);
        return data;
      }
    }

    // Fallback: collection group query
    const allSnap = await getDocs(collectionGroup(db, collName));
    const match = allSnap.docs.find(d => d.id === contentId);
    if (!match) return null;

    const data = { id: match.id, ...match.data() };
    const pathParts = match.ref.path.split('/');
    data.authorId = pathParts[1];

    try {
      const authorSnap = await getDoc(doc(db, 'authors', data.authorId));
      if (authorSnap.exists()) {
        const a = authorSnap.data();
        setAuthorName(a.penName || a.name || '');
      }
    } catch { /* skip */ }

    setContent(data);
    return data;
  }, [collName, contentId, authorId]);

  useEffect(() => {
    if (!authReady) return;
    if (!user) {
      window.location.href = '/reader';
      return;
    }
    loadContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authReady, user]);

  async function loadContent() {
    try {
      const contentData = await findContent();
      if (!contentData) {
        setError('Content not found.');
        setStatus('error');
        return;
      }
      if (!contentData.preOrder?.preOrderEnabled) {
        setError('Pre-orders are not available for this title.');
        setStatus('error');
        return;
      }
      setStatus('ready');
    } catch (err) {
      setError(err.message);
      setStatus('error');
    }
  }

  async function createPreOrder() {
    const fnName = isAudiobook ? 'createAudiobookPreOrder' : 'createEbookPreOrder';
    const createFn = httpsCallable(functions, fnName);
    const payload = isAudiobook
      ? { audiobookId: contentId, authorId: content.authorId }
      : { ebookId: contentId, authorId: content.authorId };

    const result = await createFn(payload);
    setClientSecret(result.data.clientSecret);
    setPreOrderId(result.data.preOrderId);
    return result.data;
  }

  async function confirmPreOrder() {
    const fnName = isAudiobook ? 'confirmAudiobookPreOrder' : 'confirmEbookPreOrder';
    const confirmFn = httpsCallable(functions, fnName);
    const payload = isAudiobook
      ? { preOrderId, audiobookId: contentId, authorId: content.authorId }
      : { preOrderId, ebookId: contentId, authorId: content.authorId };

    await confirmFn(payload);
  }

  if (status === 'loading') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-teal-600" />
          <p className="mt-4 text-sm text-slate-500">Loading...</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-slate-200">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
              {isAudiobook
                ? <Headphones className="h-8 w-8 text-red-400" />
                : <BookOpen className="h-8 w-8 text-red-400" />}
            </div>
            <h2 className="mb-2 text-lg font-semibold text-slate-800">Unavailable</h2>
            <p className="mb-6 text-sm text-slate-500">{error}</p>
            <Button onClick={() => { window.location.href = '/reader'; }} variant="outline" className="gap-1.5 border-slate-200">
              <ArrowLeft className="h-4 w-4" /> Back to portal
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
        <Card className="w-full max-w-md border-slate-200">
          <CardContent className="py-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-50">
              <CheckCircle className="h-8 w-8 text-green-500" />
            </div>
            <h2 className="mb-2 text-lg font-semibold text-slate-800">You're on the list!</h2>
            <p className="mb-6 text-sm text-slate-500">
              Your card will be charged when the {isAudiobook ? 'audiobook' : 'book'} launches.
              You'll get immediate access once it's released.
            </p>
            <Button onClick={() => { window.location.href = '/reader'; }} className="gap-1.5 bg-teal-600 text-white hover:bg-teal-700">
              <ArrowLeft className="h-4 w-4" /> Back to browsing
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Ready — show pre-order card
  const price = Number(content?.preOrder?.preOrderPrice || content?.price || 0);
  const releaseDate = content?.preOrder?.preOrderReleaseDate;

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-50 p-4">
      <Card className="w-full max-w-md border-slate-200 overflow-hidden">
        {content?.coverUrl && (
          <div className="relative aspect-[3/2] bg-slate-100">
            <img src={content.coverUrl} alt={content.title || ''} className="h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <Badge className="absolute left-3 bottom-3 bg-blue-600 text-white border-0 text-xs font-semibold">
              Pre-Order
            </Badge>
          </div>
        )}

        <CardContent className="space-y-4 p-6">
          <div>
            <h2 className="text-xl font-bold text-slate-900">
              {content?.title || (isAudiobook ? 'Audiobook' : 'eBook')}
            </h2>
            {authorName && <p className="mt-1 text-sm text-slate-500">by {authorName}</p>}
          </div>

          {releaseDate && (
            <p className="flex items-center gap-1.5 text-sm text-blue-600">
              <Calendar className="h-4 w-4" />
              Expected {(releaseDate.toDate ? releaseDate.toDate() : new Date(releaseDate)).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
            </p>
          )}

          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-center">
            <p className="text-2xl font-bold text-slate-900">{'\u00A3'}{price.toFixed(2)}</p>
            <p className="mt-1 text-xs text-blue-600">Card authorised now — charged on release</p>
          </div>

          <Elements stripe={stripePromise}>
            <PreOrderForm
              price={price}
              onCreatePreOrder={createPreOrder}
              onConfirmPreOrder={confirmPreOrder}
              onSuccess={() => setStatus('success')}
              onError={setError}
              isAudiobook={isAudiobook}
            />
          </Elements>

          {error && (
            <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
          )}

          <Button onClick={() => { window.location.href = '/reader'; }} variant="ghost" className="w-full gap-1.5 text-slate-500">
            <ArrowLeft className="h-4 w-4" /> Back to browsing
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}

function PreOrderForm({ price, onCreatePreOrder, onConfirmPreOrder, onSuccess, onError, isAudiobook }) {
  const stripe = useStripe();
  const elements = useElements();
  const [isProcessing, setIsProcessing] = useState(false);
  const [cardReady, setCardReady] = useState(false);

  async function handlePreOrder() {
    if (!stripe || !elements || isProcessing) return;
    setIsProcessing(true);
    onError(null);

    try {
      // 1. Create the payment intent on the server
      const { clientSecret } = await onCreatePreOrder();

      // 2. Confirm the card payment (authorise only — manual capture)
      const { error: stripeError, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardElement),
        },
      });

      if (stripeError) {
        onError(stripeError.message);
        setIsProcessing(false);
        return;
      }

      if (paymentIntent.status === 'requires_capture') {
        // 3. Confirm pre-order on the server
        await onConfirmPreOrder();
        onSuccess();
      } else {
        onError(`Unexpected payment status: ${paymentIntent.status}`);
      }
    } catch (err) {
      onError(err.message || 'Something went wrong. Please try again.');
    }

    setIsProcessing(false);
  }

  return (
    <div className="space-y-4">
      <div>
        <label className="mb-2 block text-sm font-medium text-slate-700">Card details</label>
        <div className="rounded-lg border border-slate-200 bg-white p-3">
          <CardElement
            onChange={e => setCardReady(e.complete)}
            options={{
              style: {
                base: {
                  fontSize: '16px',
                  color: '#1e293b',
                  '::placeholder': { color: '#94a3b8' },
                },
                invalid: { color: '#ef4444' },
              },
            }}
          />
        </div>
      </div>

      <Button
        onClick={handlePreOrder}
        disabled={isProcessing || !stripe || !cardReady}
        className="w-full gap-2 bg-blue-600 text-white hover:bg-blue-700"
        size="lg"
      >
        {isProcessing ? (
          <><Loader2 className="h-4 w-4 animate-spin" /> Authorising...</>
        ) : (
          <><ShoppingCart className="h-4 w-4" /> Pre-Order for {'\u00A3'}{price.toFixed(2)}</>
        )}
      </Button>
    </div>
  );
}
